import { FormDataForCatalogue, ProductDetails } from './types';
import { isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { v4 as generateUuid } from 'uuid';
import { isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import { PartnerQuoteLineItems } from '../../types';
import { isFreeEdition } from '../process-free-editions';

const setChargeElement = (productId: string) => {
  return isStatuspage(productId) ? 'unit' : isJSMProduct(productId) ? 'agent' : 'user';
};

export const formatLineItems = (
  formData: any,
  getSelectedPricingPlanForAProduct: (productId: string) => string | undefined,
  isJPDAsJiraFamilyEnabled?: boolean
): PartnerQuoteLineItems => {
  const typedData = (formData as unknown) as FormDataForCatalogue;

  if (!typedData) {
    return [];
  }

  // jira apps should have max number of user counts from jira family products
  const jiraLineItems = typedData?.product?.filter((product) =>
    isProductBelongsToJiraFamily(product.id, { isJPDAsJiraFamilyEnabled })
  );
  let maxUserCountOfJiraFamily = 0;
  if (jiraLineItems?.length) {
    const jiraProductWithMaxUser = jiraLineItems?.reduce((previous, current) => {
      const previousUsers = typedData?.numberOfUsers?.[previous.id];
      const currentUsers = typedData?.numberOfUsers?.[current.id];
      return Number(previousUsers) < Number(currentUsers) ? current : previous;
    });
    maxUserCountOfJiraFamily = jiraProductWithMaxUser && typedData?.numberOfUsers?.[jiraProductWithMaxUser?.id];
  }

  const lineItems =
    typedData?.product?.map((product) => {
      const userTierLabel = typedData?.userTier?.[product.id]?.value ?? '';
      const totalPriceOfApps = 0;
      const isItAJiraProduct = isProductBelongsToJiraFamily(product.id, { isJPDAsJiraFamilyEnabled });
      const estimatedPrice = typedData?.estimate?.[product.id] + totalPriceOfApps;
      const numberOfUsers = typedData?.numberOfUsers?.[product.id] ?? 1;
      return {
        lineItemId: generateUuid(),
        ...product,
        offeringKey: typedData?.plan?.[product.id]?.value ?? '',
        offeringName: typedData?.plan?.[product.id]?.label ?? '',
        isFreeProduct: isFreeEdition(typedData?.plan?.[product.id]?.pricingType),
        userTierLabel,
        userTierName: typedData?.userTier?.[product.id]?.label ?? '',
        pricingPlanType: 'COMMERCIAL',
        pricingPlanId: getSelectedPricingPlanForAProduct(product.id),
        pricingPlanLabel: '',
        chargeQuantities: [
          {
            chargeElement: setChargeElement(product.id),
            quantity: numberOfUsers,
          },
        ],
        billingCycleLabel: PricingPlanCycle.MONTHLY,
        productKey: product.id,
        promotions: [],
        appLineItems: typedData?.apps?.[product.id]
          ? Array.isArray(typedData?.apps?.[product.id])
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              typedData?.apps?.[product.id]?.map((app) => ({
                ...app,
                estimatePrice: app.price ?? 0,
                lineItemId: generateUuid(),
                estimatedNumberOfUsers:
                  isItAJiraProduct && maxUserCountOfJiraFamily ? maxUserCountOfJiraFamily : numberOfUsers,
                chargeQuantities: [
                  {
                    chargeElement: setChargeElement(product.id),
                    quantity: isItAJiraProduct && maxUserCountOfJiraFamily ? maxUserCountOfJiraFamily : numberOfUsers,
                  },
                ],
                billingCycleLabel: PricingPlanCycle.MONTHLY,
              })) ?? []
            : [
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  estimatePrice: typedData?.apps?.[product.id]?.price ?? 0,
                  lineItemId: generateUuid(),
                  estimatedNumberOfUsers:
                    isItAJiraProduct && maxUserCountOfJiraFamily ? maxUserCountOfJiraFamily : numberOfUsers,
                  chargeQuantities: [
                    {
                      chargeElement: setChargeElement(product.id),
                      quantity: isItAJiraProduct && maxUserCountOfJiraFamily ? maxUserCountOfJiraFamily : numberOfUsers,
                    },
                  ],
                  billingCycleLabel: PricingPlanCycle.MONTHLY,
                },
              ]
          : [],
        totalPriceOfApps,
        estimatedPrice: estimatedPrice ?? 0,
        estimatedNumberOfUsers: numberOfUsers,
        isItAJiraProduct,
      };
    }) ?? [];

  return lineItems;
};
